/**
 * AI Usage Card Component
 *
 * Displays AI usage metrics and analytics on the dashboard
 * Shows: total generations, success rate, tokens, costs, and usage trend chart
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  Sparkles,
  TrendingUp,
  Zap,
  DollarSign,
  Activity,
  ExternalLink
} from 'lucide-react';
import { getAIUsageDashboard, formatCost, formatTokens, formatNumber } from '@/services/api/aiUsageApi';

export function AIUsageCard() {
  const navigate = useNavigate();

  // Fetch dashboard data
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-usage-dashboard'],
    queryFn: getAIUsageDashboard,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });

  const dashboard = data?.data;

  // Loading state
  if (isLoading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            AI Usage
          </CardTitle>
          <CardDescription>Your AI generation activity</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (isError || !dashboard) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            AI Usage
          </CardTitle>
          <CardDescription>Your AI generation activity</CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <p className="text-sm text-muted-foreground">
            Unable to load AI usage data. Please try again later.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Empty state (no usage yet)
  if (dashboard.summary.totalGenerations === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            AI Usage
          </CardTitle>
          <CardDescription>Your AI generation activity</CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="text-center py-6">
            <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-2">No AI usage yet</p>
            <p className="text-xs text-muted-foreground">
              Try generating a page with AI to see your stats here
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data (aggregate by date across all providers)
  const chartData = dashboard.timeline
    .reduce<Array<{ date: string; requests: number }>>((acc, point) => {
      const existing = acc.find(item => item.date === point.date);
      if (existing) {
        existing.requests += point.requests;
      } else {
        acc.push({
          date: point.date,
          requests: point.requests
        });
      }
      return acc;
    }, [])
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-14); // Last 14 days for compact chart

  const { summary } = dashboard;
  const { last7Days } = summary;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              AI Usage
            </CardTitle>
            <CardDescription>Last 7 days activity</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() => navigate('/ai-usage')}
          >
            View Details
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Total Generations */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <div className="p-2 bg-primary/10 rounded-md">
              <Activity className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Generations</p>
              <p className="text-lg font-semibold">{formatNumber(last7Days.generations)}</p>
              {last7Days.successRate > 0 && (
                <Badge variant="secondary" className="text-xs mt-1">
                  {last7Days.successRate.toFixed(0)}% success
                </Badge>
              )}
            </div>
          </div>

          {/* Total Tokens */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <div className="p-2 rounded-md" style={{ backgroundColor: 'hsl(var(--feedback-info) / 0.1)' }}>
              <Zap className="h-4 w-4" style={{ color: 'hsl(var(--feedback-info))' }} />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Tokens</p>
              <p className="text-lg font-semibold">{formatTokens(last7Days.tokensUsed)}</p>
              {last7Days.cacheHitRate > 0 && (
                <Badge variant="secondary" className="text-xs mt-1">
                  {last7Days.cacheHitRate.toFixed(0)}% cached
                </Badge>
              )}
            </div>
          </div>

          {/* Total Cost */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <div className="p-2 rounded-md" style={{ backgroundColor: 'hsl(var(--feedback-success) / 0.1)' }}>
              <DollarSign className="h-4 w-4" style={{ color: 'hsl(var(--feedback-success))' }} />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Cost</p>
              <p className="text-lg font-semibold">{formatCost(last7Days.totalCost)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatCost(summary.last30Days.totalCost)} / 30d
              </p>
            </div>
          </div>

          {/* Avg Response Time */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <div className="p-2 rounded-md" style={{ backgroundColor: 'hsl(var(--feedback-warning) / 0.1)' }}>
              <TrendingUp className="h-4 w-4" style={{ color: 'hsl(var(--feedback-warning))' }} />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Avg Speed</p>
              <p className="text-lg font-semibold">
                {last7Days.avgResponseTime >= 1000
                  ? `${(last7Days.avgResponseTime / 1000).toFixed(1)}s`
                  : `${last7Days.avgResponseTime.toFixed(0)}ms`}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Response time</p>
            </div>
          </div>
        </div>

        {/* Mini Trend Chart */}
        {chartData.length > 1 && (
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">14-day trend</p>
            <ResponsiveContainer width="100%" height={80}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  tick={false}
                  axisLine={false}
                />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: 'hsl(var(--card-foreground))'
                  }}
                  labelFormatter={(value) => {
                    // Format date for tooltip
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                  formatter={(value: number) => [value, 'Generations']}
                />
                <Line
                  type="monotone"
                  dataKey="requests"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
